import { useState, useEffect } from "react";
import axios from "axios";
import { FileText, CheckCircle, Loader, AlertCircle, Trash2, RefreshCw, Upload } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getFriendlyUploadError } from "../lib/utils";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const statusColor = {
  processed: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border border-amber-200",
  processing: "bg-blue-50 text-blue-700 border border-blue-200",
  error: "bg-red-50 text-red-700 border border-red-200"
};
const statusIcon = { processed: CheckCircle, pending: Loader, processing: Loader, error: AlertCircle };

export default function Reports() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchDocs = async () => {
    try {
      const { data } = await axios.get(`${API}/documents`, { withCredentials: true });
      setDocs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocs();
    const interval = setInterval(fetchDocs, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleDelete = async (id) => {
    await axios.delete(`${API}/documents/${id}`, { withCredentials: true });
    setDocs(prev => prev.filter(d => d._id !== id));
  };

  const byType = docs.reduce((acc, d) => {
    if (!acc[d.document_type]) acc[d.document_type] = [];
    acc[d.document_type].push(d);
    return acc;
  }, {});

  const totalProcessed = docs.filter(d => d.processing_status === "processed").length;
  const totalTransactions = docs.reduce((s, d) => s + (d.transaction_count || 0), 0);

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Reports & Documents</h1>
          <p className="text-slate-500 text-sm mt-1">All uploaded and processed documents</p>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchDocs} className="border border-slate-300 bg-white text-slate-600 px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-slate-50 transition-all" data-testid="refresh-reports-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={() => navigate("/app/upload")} className="bg-[#2563EB] text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2 hover:bg-[#1D4ED8] transition-all" data-testid="reports-upload-btn">
            <Upload className="w-4 h-4" /> Upload More
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Total Documents</p>
          <p className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Manrope" }}>{docs.length}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Processed</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1" style={{ fontFamily: "Manrope" }}>{totalProcessed}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <p className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Transactions Extracted</p>
          <p className="text-2xl font-bold text-blue-600 mt-1" style={{ fontFamily: "Manrope" }}>{totalTransactions}</p>
        </div>
      </div>

      {/* Grouped by type */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-32 bg-slate-100 rounded-xl animate-pulse" />)}
        </div>
      ) : docs.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center shadow-sm">
          <FileText className="w-12 h-12 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-600 font-semibold mb-2" style={{ fontFamily: "Manrope" }}>No documents yet</p>
          <p className="text-slate-400 text-sm mb-6">Upload your first financial document to get started</p>
          <button onClick={() => navigate("/app/upload")} className="bg-[#2563EB] text-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#1D4ED8] transition-all">
            Upload Documents
          </button>
        </div>
      ) : (
        Object.entries(byType).map(([type, typeDocs]) => {
          const StatusIcon = (doc) => { const I = statusIcon[doc.processing_status] || Loader; return I; };
          return (
            <div key={type} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid={`doc-group-${type.toLowerCase().replace(/[^a-z]/g, "-")}`}>
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <h2 className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Manrope" }}>{type}</h2>
                <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{typeDocs.length} file{typeDocs.length !== 1 ? "s" : ""}</span>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase">File Name</th>
                    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase">Transactions</th>
                    <th className="text-left px-6 py-2.5 text-xs font-semibold text-slate-500 uppercase">Uploaded</th>
                    <th className="px-6 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {typeDocs.map(doc => {
                    const Icon = statusIcon[doc.processing_status] || Loader;
                    return (
                      <tr key={doc._id} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="px-6 py-3">
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-700 font-medium">{doc.original_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-3">
                          <span className={`text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 w-fit ${statusColor[doc.processing_status]}`}>
                            <Icon className={`w-3 h-3 ${doc.processing_status === "processing" ? "animate-spin" : ""}`} />
                            {doc.processing_status}
                          </span>
                          {doc.processing_status === "error" && (
                            <p className="text-red-500 text-xs mt-1 max-w-[220px]" data-testid={`reports-doc-error-${doc._id}`}>
                              {getFriendlyUploadError(doc.error_message)}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-3 text-slate-600">{doc.transaction_count || 0}</td>
                        <td className="px-6 py-3 text-slate-400 text-xs">{new Date(doc.upload_date).toLocaleDateString()}</td>
                        <td className="px-6 py-3">
                          <button onClick={() => handleDelete(doc._id)} className="text-slate-300 hover:text-red-500 transition-colors" data-testid={`reports-delete-${doc._id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })
      )}
    </div>
  );
}
